import posthog from 'posthog-js'

import { msalInstance } from 'src/services/auth'

const INTERNAL_EMAIL_DOMAIN = '@arcadis.com'

/**
 * Identity of the signed-in user for PostHog.
 *
 * `userId` is the B2C `sub` claim and is the ONLY value that may be used as the
 * PostHog distinct id: the email address is not permitted as a user id, and is
 * not sent to PostHog in any form.
 *
 * `isInternal` is still derived from the email address, because "internal vs
 * external" has no claim of its own. The email is read locally only for that
 * boolean and never leaves the browser as a property, event or id.
 */
export interface PosthogIdentity {
  /** B2C `sub` claim — the PostHog distinct id. `undefined` when not signed in. */
  userId: string | undefined
  /** Whether the signed-in user's email is on the Arcadis domain. */
  isInternal: boolean
}

const toRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}

const getActiveAccount = () => msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]

/**
 * The `sub` claim of the active account — the PostHog distinct id.
 *
 * The raw claim is authoritative. MSAL also surfaces `sub` as `localAccountId`,
 * which is used as the fallback for a token shape that does not expose the claim
 * directly. An account that is authenticated but carries neither (a
 * service-principal/automation session) resolves to `undefined`, so no caller can
 * identify it under a placeholder.
 */
const resolveUserId = (): string | undefined => {
  const account = getActiveAccount()
  if (!account) {
    return undefined
  }

  const claimSub = toRecord(account.idTokenClaims).sub
  if (typeof claimSub === 'string' && claimSub.length > 0) {
    return claimSub
  }

  const localAccountId = account.localAccountId
  return typeof localAccountId === 'string' && localAccountId.length > 0
    ? localAccountId
    : undefined
}

const resolveEmail = (): string | undefined => {
  const account = getActiveAccount()
  if (!account) {
    return undefined
  }

  const claims = toRecord(account.idTokenClaims)
  const claimEmail = claims.email
  const claimEmails = claims.emails

  if (typeof account.username === 'string' && account.username.length > 0) {
    return account.username
  }
  if (typeof claimEmail === 'string' && claimEmail.length > 0) {
    return claimEmail
  }
  if (
    Array.isArray(claimEmails) &&
    typeof claimEmails[0] === 'string' &&
    claimEmails[0].length > 0
  ) {
    return claimEmails[0]
  }
  return undefined
}

const resolveIsInternal = (): boolean => {
  const email = resolveEmail()
  return !!email && email.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN)
}

export const resolvePosthogIdentity = (): PosthogIdentity => ({
  userId: resolveUserId(),
  isInternal: resolveIsInternal()
})

/**
 * The PostHog distinct id for the current user, or `undefined` when not signed
 * in (or when the account carries no `sub`).
 */
export const resolvePosthogUserId = (): string | undefined => resolvePosthogIdentity().userId

/**
 * Identify the signed-in user by `sub` so that the very first event after a
 * sign-in is already attributed to the right distinct id, instead of waiting for
 * the next navigation to run the router hook.
 *
 * This is idempotent — PostHog ignores a repeated identify for the same id — and
 * a no-op when not signed in. It deliberately lives next to the identity
 * resolution rather than in `posthog-events.ts`, which must stay free of any
 * import back into `services/auth` (that module already imports from
 * `posthog-events`, so an import the other way would be a cycle).
 *
 * Returns the id it identified, so callers do not have to resolve it a second
 * time just to attach it to an event.
 */
export const identifyPosthogUser = (): string | undefined => {
  const userId = resolvePosthogUserId()
  if (!userId) {
    return undefined
  }

  try {
    // A no-op until `posthog.init()` has run: identifying a not-yet-initialised
    // SDK would be dropped anyway, and the router hook identifies on first
    // navigation regardless.
    if (posthog.__loaded) {
      posthog.identify(userId)
    }
  } catch {
    // Telemetry must never break sign-in; PostHog not being initialised is fine.
  }
  return userId
}
